import { useGetPerformOnboardingJobSuggestionMutation } from "@graphql/generated";
import { Skeleton } from "@heroui/skeleton";
import { motion } from "framer-motion";
import { useEffect, useRef } from "react";
import JobPostCard, { JobPostCardSkeleton } from "./JobPostCard";

interface ISuccessStepProps {
  handleContinue: () => void;
  existingUserData: {
    industry?: string;
    jobInterest?: string[];
    country?: string;
  };
  formData: {
    industry: string;
    country: string;
    jobInterest: string[];
  };
  isLoadingContinue: boolean;
}

const SuccessStep = ({ handleContinue, existingUserData, formData, isLoadingContinue }: ISuccessStepProps) => {
  const [getPerformOnboardingJobSuggestion, { data, loading }] = useGetPerformOnboardingJobSuggestionMutation();
  const hasCalledApiRef = useRef(false);

  useEffect(() => {
    if (hasCalledApiRef.current) return;

    const industry = formData.industry || existingUserData.industry;
    const jobInterest = formData.jobInterest.length > 0 ? formData.jobInterest : existingUserData.jobInterest;
    if (industry && jobInterest) {
      hasCalledApiRef.current = true;
      getPerformOnboardingJobSuggestion({
        variables: {
          industry,
          interestedJobs: jobInterest,
        },
      });
    }
  }, [formData, existingUserData, getPerformOnboardingJobSuggestion]);

  const hasJobSuggestions = (data?.account.performOnboardingJobSuggestion?.output?.jobSuggestions?.length ?? 0) > 0;
  const isComplete = !loading;

  const getMotivationalMessage = () => {
    return "Great job completing your profile! You're well-positioned for success. We'll keep you updated as new opportunities become available!";
  };

  return (
    <div className="h-full flex flex-col">
      <div className="absolute top-0 right-0 w-64 h-64 bg-primary-50 rounded-full opacity-20 blur-3xl -translate-x-20 -translate-y-20"></div>
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-primary-50 rounded-full opacity-20 blur-3xl translate-x-20 translate-y-20"></div>

      <motion.div
        className="w-full text-center relative z-10 flex-1 flex flex-col"
        initial="hidden"
        animate="visible"
        variants={{
          hidden: { opacity: 0 },
          visible: {
            opacity: 1,
            transition: {
              when: "beforeChildren",
              staggerChildren: 0.3,
            },
          },
        }}
      >
        <motion.div
          className="w-20 h-20 md:w-28 md:h-28 backdrop-blur-sm bg-white/30 shadow-xl rounded-3xl mx-auto flex items-center justify-center mb-6 md:mb-10 relative overflow-hidden border border-white/50 flex-shrink-0"
          variants={{
            hidden: { scale: 0, rotate: -20 },
            visible: {
              scale: 1,
              rotate: 0,
              transition: {
                type: "spring",
                stiffness: 260,
                damping: 20,
              },
            },
          }}
        >
          <div className="absolute -inset-1 bg-primary-100 opacity-30 blur-md"></div>
          <div className="absolute -bottom-6 -right-6 w-12 h-12 bg-primary-200 rounded-full blur-md"></div>
          <div className="absolute -top-6 -left-6 w-12 h-12 bg-primary-200 rounded-full blur-md"></div>
          <motion.svg
            width="50"
            height="50"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="relative z-10"
          >
            <motion.path
              d="M8.5 12.5L10.5 14.5L15.5 9.5"
              stroke="#C71F2C"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.8 }}
            />
            <motion.path
              d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
              stroke="#C71F2C"
              strokeWidth="2"
              strokeLinecap="round"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.8 }}
            />
          </motion.svg>
        </motion.div>

        <motion.h2
          className="text-xl md:text-4xl font-bold mb-2 md:mb-3 relative flex-shrink-0"
          variants={{
            hidden: { opacity: 0, y: 20 },
            visible: { opacity: 1, y: 0 },
          }}
        >
          <span className="relative">Congratulations! 🎉</span>
        </motion.h2>

        <motion.p
          className="text-sm md:text-base text-gray-600 mb-6 md:mb-12 max-w-md mx-auto flex-shrink-0"
          variants={{
            hidden: { opacity: 0, y: 20 },
            visible: { opacity: 1, y: 0 },
          }}
        >
          {loading ? (
            <div className="space-y-2 mt-2">
              <Skeleton className="h-4 w-full rounded-lg">
                <div className="h-4 w-full rounded-lg bg-default-200" />
              </Skeleton>
              <Skeleton className="h-4 w-full rounded-lg">
                <div className="h-4 w-full rounded-lg bg-default-200" />
              </Skeleton>
              <Skeleton className="h-4 w-[70%] mx-auto rounded-lg">
                <div className="h-4 w-[70%] mx-auto rounded-lg bg-default-200" />
              </Skeleton>
            </div>
          ) : isComplete && !hasJobSuggestions ? (
            getMotivationalMessage()
          ) : (
            data?.account.performOnboardingJobSuggestion?.output?.text
          )}
        </motion.p>

        {(loading || hasJobSuggestions) && (
          <motion.div
            className="max-w-2xl mx-auto flex-1 flex flex-col min-h-0 w-full"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
          >
            <h3 className="md:text-xl font-semibold mb-4 md:mb-6 text-left flex items-center flex-shrink-0">
              <span className="bg-primary-500 w-3 h-3 rounded-full mr-3"></span>
              <span>Perfect matches for you</span>

              {loading ? (
                <Skeleton className="ml-2 h-6 w-6 rounded-full">
                  <div className="h-4 w-8 rounded-lg bg-default-200" />
                </Skeleton>
              ) : (
                <span className="ml-2 text-sm bg-primary-100 text-primary-700 px-2.5 py-0.5 rounded-full">
                  {data?.account.performOnboardingJobSuggestion?.output?.jobSuggestions?.length ?? 0}
                </span>
              )}
            </h3>

            <div className="flex-1 overflow-y-auto space-y-4 md:space-y-6 min-h-0">
              {loading ? (
                <>
                  <JobPostCardSkeleton index={0} />
                  <JobPostCardSkeleton index={1} />
                  <JobPostCardSkeleton index={2} />
                </>
              ) : (
                data?.account.performOnboardingJobSuggestion?.output?.jobSuggestions?.map((job, index) => (
                  <JobPostCard
                    key={job?.companyName}
                    job={job}
                    index={index}
                    handleContinue={handleContinue}
                    isLoadingContinue={isLoadingContinue}
                  />
                ))
              )}
            </div>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default SuccessStep;
